import React, { useState } from 'react';
import { adminApi } from '../../services/api';

type Result = {
  totalRows: number;
  created: number;
  skipped: number;
  errors: Array<{ row: number; phone?: string; reason: string }>;
};

export const AdminImportUsersScreen: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [errMsg, setErrMsg] = useState('');

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null);
    setResult(null);
    setErrMsg('');
  };

  const upload = async () => {
    if (!file) return;
    if (!window.confirm(
      `Import users from "${file.name}"? Users will be created immediately with the opening balances in the spreadsheet. This cannot be undone.`
    )) return;
    setBusy(true);
    setErrMsg('');
    setResult(null);
    try {
      const r = await adminApi.importUsers(file);
      setResult(r);
    } catch (e: any) {
      setErrMsg(e?.response?.data?.message || e?.message || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="admin-filters" style={{ flexWrap: 'wrap', gap: '0.75rem' }}>
        <a
          href="/templates/yebo-wallet-user-import-template.xlsx"
          download
          className="admin-btn"
          style={{ textDecoration: 'none' }}
        >
          <i className="fas fa-download"></i> Download template
        </a>
        <label className="admin-btn allocate" style={{ cursor: 'pointer' }}>
          <i className="fas fa-file-upload"></i> {file ? file.name : 'Choose .xlsx file'}
          <input
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={onChange}
            style={{ display: 'none' }}
          />
        </label>
        <button
          className="admin-btn allocate"
          onClick={upload}
          disabled={!file || busy}
        >
          {busy ? <><i className="fas fa-spinner fa-spin"></i> Importing…</> : <><i className="fas fa-upload"></i> Import users</>}
        </button>
      </div>

      <p className="admin-muted">
        Use the supplied template. Required columns: Name, Phone, PIN, and the three opening-balance columns.
        Phone must be unique. Existing users (matched by phone or email) are skipped and listed below.
      </p>

      {errMsg && <p className="admin-muted" style={{ color: '#E02416' }}><i className="fas fa-exclamation-triangle"></i> {errMsg}</p>}

      {result && (
        <div className="admin-group" style={{ marginTop: '1rem' }}>
          <div className="admin-group-header">
            <span>
              <strong>Import summary</strong>
              <span className="admin-muted"> · {result.totalRows} row(s) in file</span>
              <span className="ext-chip" style={{ background: '#00A000', color: '#fff' }}>
                <i className="fas fa-check-circle"></i> {result.created} created
              </span>
              {result.skipped > 0 && (
                <span className="ext-chip" style={{ background: '#E02416', color: '#fff' }}>
                  <i className="fas fa-times-circle"></i> {result.skipped} skipped
                </span>
              )}
            </span>
          </div>
          {result.errors.length > 0 && (
            <table className="admin-table">
              <thead>
                <tr><th>Row</th><th>Phone</th><th>Reason</th></tr>
              </thead>
              <tbody>
                {result.errors.map((e, i) => (
                  <tr key={i}>
                    <td>{e.row}</td>
                    <td>{e.phone || '-'}</td>
                    <td>{e.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {result.errors.length === 0 && result.created > 0 && (
            <p className="admin-muted" style={{ padding: '0.75rem' }}>
              <i className="fas fa-check"></i> All rows imported successfully.
            </p>
          )}
        </div>
      )}
    </div>
  );
};
